import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../lib/supabase';
import { isNetworkError } from '../../lib/isNetworkError';
import { projectsFallback, getProjectFromFallback } from '../../data/projectsFallback';

type LineKind = 'in' | 'out' | 'err' | 'welcome' | 'dim' | 'accent' | 'warn' | 'easter';

interface TerminalLine {
  kind: LineKind;
  text: string;
  prompt?: string;
  /** Optional accent-alt-ink prefix rendered before `text`. Used by easter lines
   *  so the leading marker (e.g. "↑↑↓↓←→←→ b a    —") gets the secondary accent
   *  while the description after stays in the default text color. */
  prefix?: string;
}

interface TerminalProps {
  onClose?: () => void;
  isModal?: boolean;
  onExit?: () => void;
}

const PROMPT = 'joe@hiimjoe:~$';

// Help-table / tab-complete surface. 'easter' is intentionally absent.
const COMMANDS = [
  'help',
  'whoami',
  'projects',
  'articles',
  'open',
  'theme',
  'stats',
  'contact',
  'clear',
  'exit',
];

const WELCOME: TerminalLine[] = [
  { kind: 'welcome', text: 'joe@hiimjoe — zsh' },
  { kind: 'welcome', text: "type 'help' for commands." },
  { kind: 'out', text: '' },
];

const out = (text: string): TerminalLine => ({ kind: 'out', text });
const err = (text: string): TerminalLine => ({ kind: 'err', text });
const dim = (text: string): TerminalLine => ({ kind: 'dim', text });
const accent = (text: string): TerminalLine => ({ kind: 'accent', text });
const warn = (text: string): TerminalLine => ({ kind: 'warn', text });
const easter = (text: string, prefix?: string): TerminalLine => ({ kind: 'easter', text, prefix });

const supabaseErr = (e: { message?: string } | null | undefined): TerminalLine =>
  err(`// supabase: ${e?.message ?? 'unknown error'}`);

const Terminal: React.FC<TerminalProps> = ({ onClose, isModal = false, onExit }) => {
  const [history, setHistory] = useState<TerminalLine[]>(WELCOME);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showCursor, setShowCursor] = useState(true);
  const [knownSlugs, setKnownSlugs] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const { setTeam, setModePreference, team, mode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // preventScroll: the embedded terminal on HomePage sits below the fold,
    // so a plain .focus() on mount drags the page down to it.
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const id = setInterval(() => setShowCursor(c => !c), 530);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  const focusInput = () => inputRef.current?.focus();
  const emit = (lines: TerminalLine[]) => setHistory(prev => [...prev, ...lines]);

  const runProjects = useCallback(async (): Promise<TerminalLine[]> => {
    let rows: Array<{
      slug: string;
      name: string;
      status: string | null;
      tagline: string | null;
    }>;
    try {
      const { data, error: e } = await supabase
        .from('projects')
        .select('slug, name, status, tagline')
        .neq('status', 'private')
        .order('year_completed', { ascending: false, nullsFirst: false });
      if (e) throw new Error(e.message);
      rows = (data ?? []) as Array<{
        slug: string;
        name: string;
        status: string | null;
        tagline: string | null;
      }>;
    } catch (caught) {
      if (!isNetworkError(caught)) {
        return [
          supabaseErr({ message: caught instanceof Error ? caught.message : String(caught) }),
          out(''),
        ];
      }
      rows = projectsFallback.map(p => ({
        slug: p.slug,
        name: p.name,
        status: p.status,
        tagline: p.tagline,
      }));
    }
    if (rows.length === 0) return [dim('// no projects loaded'), out('')];
    setKnownSlugs(prev => Array.from(new Set([...prev, ...rows.map(r => r.slug)])));
    const lines: TerminalLine[] = rows.map(r => {
      const body = `${r.slug.padEnd(18)}  —  ${r.tagline ?? r.name}`;
      if (r.status === 'active') return accent(body);
      if (r.status === 'coming_soon') return warn(body);
      return dim(body);
    });
    return [...lines, out('')];
  }, []);

  const runArticles = useCallback(async (): Promise<TerminalLine[]> => {
    const { data, error: e } = await supabase
      .from('articles')
      .select('slug, title, publish_date')
      .eq('status', 'published')
      .order('publish_date', { ascending: false });
    if (e) return [supabaseErr(e), out('')];
    const rows = (data ?? []) as Array<{
      slug: string | null;
      title: string;
      publish_date: string | null;
    }>;
    if (rows.length === 0) return [dim('// no published articles yet'), out('')];
    setKnownSlugs(prev =>
      Array.from(new Set([...prev, ...rows.map(r => r.slug || '').filter(Boolean)]))
    );
    const lines: TerminalLine[] = rows.map(r =>
      out(`${(r.slug ?? '—').padEnd(18)}  —  ${r.title}`)
    );
    return [...lines, out('')];
  }, []);

  const runOpen = useCallback(
    async (slug: string): Promise<TerminalLine[]> => {
      if (!slug) {
        return [err('usage: open <slug>'), out('')];
      }

      let project: { slug: string; name: string } | null = null;
      let networkDown = false;
      try {
        const { data, error: projErr } = await supabase
          .from('projects')
          .select('slug, name')
          .eq('slug', slug)
          .neq('status', 'private')
          .maybeSingle();
        if (projErr) throw new Error(projErr.message);
        project = (data as { slug: string; name: string } | null) ?? null;
      } catch (caught) {
        if (!isNetworkError(caught)) {
          return [
            supabaseErr({ message: caught instanceof Error ? caught.message : String(caught) }),
            out(''),
          ];
        }
        networkDown = true;
        const fb = getProjectFromFallback(slug);
        if (fb) project = { slug: fb.slug, name: fb.name };
      }
      if (project) {
        const p = project;
        setTimeout(() => navigate(`/work/${p.slug}`), 300);
        return [out(`opening project: ${p.name}`), dim(`// → /work/${p.slug}`), out('')];
      }
      if (networkDown) {
        return [err(`not found: ${slug}`), out('')];
      }

      const { data: article, error: artErr } = await supabase
        .from('articles')
        .select('slug, title')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();
      if (artErr) return [supabaseErr(artErr), out('')];
      if (article) {
        const a = article as { slug: string; title: string };
        setTimeout(() => navigate(`/articles/${a.slug}`), 300);
        return [out(`opening article: ${a.title}`), dim(`// → /articles/${a.slug}`), out('')];
      }

      return [err(`not found: ${slug}`), out('')];
    },
    [navigate]
  );

  const runStats = useCallback(async (): Promise<TerminalLine[]> => {
    // Three parallel reads. chat_sessions + site_analytics are RLS-locked for
    // anon, so we route through the get_public_site_stats RPC rather than
    // direct .count queries (which silently return 0). Projects SELECT is
    // public so we could go direct, but piggybacking on the same RPC keeps
    // it a single round trip.
    const { data, error: e } = await supabase.rpc('get_public_site_stats');
    if (e) return [supabaseErr(e), out('')];
    const s = data as {
      projects_live: number;
      chats_7d: number;
      visitors_7d: number;
    };
    return [
      out(
        `${s.visitors_7d} visits · ${s.chats_7d} chats · ${s.projects_live} projects live · last 7d`
      ),
      out(''),
    ];
  }, []);

  const executeCommand = useCallback(
    async (raw: string) => {
      const trimmed = raw.trim();
      const lower = trimmed.toLowerCase();
      const [command, ...args] = lower.split(/\s+/);

      const inLine: TerminalLine = { kind: 'in', text: raw, prompt: PROMPT };

      if (command === 'clear') {
        setHistory([]);
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      if (command === 'exit') {
        onClose?.();
        onExit?.();
        return;
      }
      if (command === '') return;

      emit([inLine]);

      let output: TerminalLine[] = [];

      switch (command) {
        case 'help':
          output = [
            out('available commands:'),
            out('  help              — this'),
            out('  whoami            — about joe'),
            out('  projects          — list live projects'),
            out('  articles          — list published articles'),
            out('  open <slug>       — open project or article'),
            out('  theme [h|p|d|l]   — switch palette / mode'),
            out('  stats             — live site stats'),
            out('  contact           — get in touch'),
            out('  clear             — clear screen'),
            out('  exit              — close terminal'),
            out(''),
          ];
          break;

        case 'whoami':
          output = [
            out('joe hamilton — ai systems architect, charlotte nc.'),
            out('co-founder + cto, servet digital.'),
            out('builds in: typescript, react, supabase, claude, gpt, gemini, perplexity.'),
            out(''),
          ];
          break;

        case 'projects':
          output = await runProjects();
          break;

        case 'articles':
          output = await runArticles();
          break;

        case 'open':
          output = await runOpen(args[0] ?? '');
          break;

        case 'theme': {
          const flag = args[0];
          let nextTeam = team;
          let nextMode = mode;
          if (flag === 'h' || flag === 'hornets') {
            setTeam('hornets');
            nextTeam = 'hornets';
          } else if (flag === 'p' || flag === 'panthers') {
            setTeam('panthers');
            nextTeam = 'panthers';
          } else if (flag === 'd' || flag === 'dark') {
            setModePreference('dark');
            nextMode = 'dark';
          } else if (flag === 'l' || flag === 'light') {
            setModePreference('light');
            nextMode = 'light';
          } else {
            output = [
              err('usage: theme [h|p|d|l]'),
              out('  h = hornets · p = panthers'),
              out('  d = dark · l = light'),
              out(''),
            ];
            break;
          }
          output = [out(`ok · ${nextTeam} · ${nextMode}`), out('')];
          break;
        }

        case 'stats':
          output = await runStats();
          break;

        case 'contact':
          output = [
            out('email: j.hamilton@servetdigital.com'),
            out('linkedin: in/joehamiltondev'),
            dim('or: /contact'),
            out(''),
          ];
          break;

        case 'easter':
          // hidden — not listed in help, not tab-completed.
          // konami: App.tsx listens for ↑↑↓↓←→←→ b a and opens DebugQuestGame.
          // f12: window.joeAI banner printed by src/lib/joeAI-console.ts.
          output = [
            easter('ok. you found it.'),
            easter('try the konami sequence on the page', '  ↑↑↓↓←→←→ b a    — '),
            easter('say hi in the console', '  f12              — '),
            out(''),
          ];
          break;

        default:
          output = [err(`command not found: ${command}  (try 'help')`), out('')];
      }

      emit(output);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [
      onClose,
      onExit,
      setTeam,
      setModePreference,
      team,
      mode,
      runProjects,
      runArticles,
      runOpen,
      runStats,
    ]
  );

  const completeTab = () => {
    if (!input) return;
    const lower = input.toLowerCase();

    if (!lower.includes(' ')) {
      const cmdMatches = COMMANDS.filter(c => c.startsWith(lower));
      if (cmdMatches.length === 1) {
        setInput(cmdMatches[0]);
        return;
      }
      if (cmdMatches.length > 1) {
        emit([out(''), out(cmdMatches.join('  ')), out('')]);
      }
      return;
    }

    // slug completion for `open <partial>`
    const openMatch = lower.match(/^open\s+(.*)$/);
    if (openMatch) {
      const partial = openMatch[1];
      const matches = knownSlugs.filter(s => s.startsWith(partial));
      if (matches.length === 1) {
        setInput(`open ${matches[0]}`);
        return;
      }
      if (matches.length > 1) {
        emit([out(''), out(matches.join('  ')), out('')]);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (input.trim()) {
        setCommandHistory(prev => [...prev, input]);
        setHistoryIndex(-1);
      }
      void executeCommand(input);
      setInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const nextIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
        setHistoryIndex(nextIndex);
        setInput(commandHistory[commandHistory.length - 1 - nextIndex] || '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const nextIndex = historyIndex - 1;
        setHistoryIndex(nextIndex);
        setInput(commandHistory[commandHistory.length - 1 - nextIndex] || '');
      } else {
        setHistoryIndex(-1);
        setInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      completeTab();
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setHistory([]);
    } else if (e.key === 'c' && e.ctrlKey) {
      e.preventDefault();
      emit([{ kind: 'in', text: input + '^C', prompt: PROMPT }]);
      setInput('');
    } else if (e.key === 'Escape' && isModal) {
      onClose?.();
    }
  };

  return (
    <div
      className={`flex flex-col font-mono overflow-hidden rounded-xs border border-border bg-bg-sunken ${
        isModal ? 'w-full h-[70vh] sm:h-[500px]' : 'w-full h-full'
      }`}
      onClick={focusInput}
      style={{ color: 'var(--text)' }}
    >
      <div className="flex items-center gap-2.5 px-s3 py-2 border-b border-border flex-shrink-0 text-[10px] uppercase tracking-[0.8px] text-text-dim">
        <span className="text-accent">●</span>
        <span>joe@hiimjoe — zsh — 80×24</span>
        <span className="flex-1" />
        <span className="hidden sm:inline">type 'help'</span>
        {isModal && onClose && (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            className="ml-2 text-text-dim hover:text-text transition-colors p-1"
            aria-label="Close terminal"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div
        ref={outputRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-[14px] py-s3 text-[12px] leading-[1.55]"
      >
        {history.map((line, i) => {
          if (line.kind === 'in') {
            return (
              <div key={i} className="whitespace-pre-wrap break-words text-accent">
                <span>{line.prompt || PROMPT}</span> <span>{line.text}</span>
              </div>
            );
          }
          if (line.kind === 'easter') {
            return (
              <div
                key={i}
                className="whitespace-pre-wrap break-words text-text border-l-2 border-l-accent-alt-ink pl-3"
              >
                {line.prefix && <span className="text-accent-alt-ink">{line.prefix}</span>}
                {line.text || (line.prefix ? '' : ' ')}
              </div>
            );
          }
          const cls =
            line.kind === 'err'
              ? 'text-err'
              : line.kind === 'welcome'
              ? 'text-text-muted'
              : line.kind === 'dim'
              ? 'text-text-dim'
              : line.kind === 'accent'
              ? 'text-accent'
              : line.kind === 'warn'
              ? 'text-warn'
              : 'text-text';
          return (
            <div key={i} className={`whitespace-pre-wrap break-words ${cls}`}>
              {line.text || ' '}
            </div>
          );
        })}

        <div className="flex items-center gap-2">
          <span className="text-accent">{PROMPT}</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 min-w-0 bg-transparent border-none outline-none text-text text-[12px]"
            spellCheck={false}
            autoComplete="off"
            aria-label="Terminal input"
          />
          <span
            className={`inline-block w-2 h-[14px] bg-accent transition-opacity ${
              showCursor ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default Terminal;
